/**
 * Stripe-style services grid — Yön C.
 * Cards are wider, 2 columns. Left = text, right = abstract illustration.
 * Each card has a unique top gradient bar and customer proof badge.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Check } from 'lucide-react';
import { SERVICES } from '../data/mock';

const META = {
  'business-voip':   { stripe: 'from-violet-500 to-fuchsia-500', proof: 'Used by 500+ UK SMBs', from: '£11/user/mo', viz: 'phone' },
  'sip-trunk':       { stripe: 'from-emerald-500 to-teal-500',   proof: '99.99% uptime SLA',     from: '£5/channel/mo', viz: 'pipes' },
  'microsoft-teams': { stripe: 'from-blue-500 to-indigo-600',    proof: 'Microsoft certified',  from: '£8/user/mo',  viz: 'teams' },
  'inbound':         { stripe: 'from-amber-500 to-orange-500',   proof: '4M+ calls handled',    from: '£24/agent/mo', viz: 'queue' },
  'outbound':        { stripe: 'from-rose-500 to-pink-500',      proof: '2× agent productivity', from: '£32/agent/mo', viz: 'dial' },
  'web-phone':       { stripe: 'from-sky-500 to-cyan-500',       proof: 'No install required',  from: 'Included',  viz: 'browser' },
  'video':           { stripe: 'from-teal-500 to-emerald-500',   proof: 'HD 1080p',             from: 'Included',  viz: 'video' },
  'unified-comms':   { stripe: 'from-indigo-500 to-violet-500',  proof: 'All channels in one',   from: '£14/user/mo', viz: 'chat' },
  'phone-features':  { stripe: 'from-slate-500 to-slate-700',    proof: '40+ features',         from: 'Included',  viz: 'features' },
};

function Icon({ name, size = 22, className = '' }) {
  const Ic = Icons[name] || Icons.Sparkles;
  return <Ic size={size} className={className} />;
}

/** Tiny abstract SVG illustration that matches each service's visual language. */
function Viz({ kind, className = '' }) {
  switch (kind) {
    case 'phone':
      return (
        <svg viewBox="0 0 200 200" className={className}>
          <defs>
            <linearGradient id="vphone" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#a78bfa" /><stop offset="1" stopColor="#e879f9" />
            </linearGradient>
          </defs>
          <rect x="55" y="20" width="90" height="160" rx="16" fill="url(#vphone)" opacity="0.18" />
          <rect x="60" y="25" width="80" height="150" rx="12" fill="url(#vphone)" />
          <circle cx="100" cy="155" r="6" fill="#fff" opacity=".6" />
          <rect x="72" y="40" width="56" height="6" rx="3" fill="#fff" opacity=".7" />
          <rect x="72" y="56" width="40" height="4" rx="2" fill="#fff" opacity=".4" />
          <rect x="72" y="80" width="56" height="40" rx="6" fill="#fff" opacity=".15" />
          <rect x="78" y="95" width="44" height="3" rx="1.5" fill="#fff" opacity=".8" />
          <rect x="78" y="103" width="32" height="3" rx="1.5" fill="#fff" opacity=".6" />
          <rect x="78" y="111" width="40" height="3" rx="1.5" fill="#fff" opacity=".4" />
        </svg>
      );
    case 'pipes':
      return (
        <svg viewBox="0 0 200 200" className={className}>
          <defs>
            <linearGradient id="vpipe" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#34d399" /><stop offset="1" stopColor="#06b6d4" /></linearGradient>
          </defs>
          {[0, 30, 60, 90, 120].map((y, i) => (
            <rect key={i} x="20" y={40 + y / 2} width="160" height="6" rx="3" fill="url(#vpipe)" opacity={1 - i * 0.15} />
          ))}
          <circle cx="60" cy="46" r="8" fill="#10b981" />
          <circle cx="140" cy="61" r="8" fill="#0ea5e9" />
          <circle cx="100" cy="76" r="8" fill="#34d399" />
        </svg>
      );
    case 'teams':
      return (
        <svg viewBox="0 0 200 200" className={className}>
          <rect x="20" y="30" width="160" height="100" rx="14" fill="#3b82f6" opacity=".18" />
          <rect x="30" y="40" width="55" height="38" rx="6" fill="#3b82f6" />
          <rect x="92" y="40" width="55" height="38" rx="6" fill="#6366f1" />
          <rect x="30" y="84" width="55" height="38" rx="6" fill="#6366f1" />
          <rect x="92" y="84" width="55" height="38" rx="6" fill="#3b82f6" />
          <text x="100" y="160" fill="#3b82f6" fontFamily="system-ui" fontWeight="800" fontSize="22" textAnchor="middle">Teams</text>
        </svg>
      );
    case 'queue':
      return (
        <svg viewBox="0 0 200 200" className={className}>
          {[0, 1, 2, 3].map((i) => (
            <g key={i} transform={`translate(0,${i * 32})`}>
              <rect x="25" y="40" width="150" height="22" rx="11" fill="#f59e0b" opacity={0.85 - i * 0.18} />
              <circle cx="36" cy="51" r="6" fill="#fff" opacity=".7" />
              <rect x="50" y="48" width="60" height="6" rx="3" fill="#fff" opacity=".6" />
            </g>
          ))}
        </svg>
      );
    case 'dial':
      return (
        <svg viewBox="0 0 200 200" className={className}>
          <circle cx="100" cy="100" r="70" fill="#f43f5e" opacity=".15" />
          <circle cx="100" cy="100" r="55" fill="none" stroke="#f43f5e" strokeWidth="3" strokeDasharray="6 4" />
          <circle cx="100" cy="100" r="35" fill="#f43f5e" />
          <path d="M88 95 l24 5 -24 5 z" fill="#fff" />
        </svg>
      );
    case 'browser':
      return (
        <svg viewBox="0 0 200 200" className={className}>
          <rect x="20" y="30" width="160" height="120" rx="10" fill="#0ea5e9" opacity=".15" />
          <rect x="20" y="30" width="160" height="20" rx="10" fill="#0ea5e9" />
          <circle cx="34" cy="40" r="3" fill="#fff" /><circle cx="46" cy="40" r="3" fill="#fff" /><circle cx="58" cy="40" r="3" fill="#fff" />
          <rect x="40" y="70" width="120" height="10" rx="5" fill="#0ea5e9" opacity=".7" />
          <rect x="40" y="86" width="80" height="6" rx="3" fill="#0ea5e9" opacity=".5" />
          <rect x="40" y="100" width="100" height="6" rx="3" fill="#0ea5e9" opacity=".4" />
          <circle cx="100" cy="135" r="14" fill="#0ea5e9" />
          <path d="M95 130 l12 5 -12 5 z" fill="#fff" />
        </svg>
      );
    case 'video':
      return (
        <svg viewBox="0 0 200 200" className={className}>
          <rect x="30" y="50" width="100" height="70" rx="8" fill="#14b8a6" opacity=".2" />
          <rect x="40" y="60" width="80" height="50" rx="4" fill="#14b8a6" />
          <path d="M140 70 l30 -15 v90 l-30 -15 z" fill="#14b8a6" />
          <circle cx="80" cy="85" r="14" fill="#fff" opacity=".7" />
        </svg>
      );
    case 'chat':
      return (
        <svg viewBox="0 0 200 200" className={className}>
          <rect x="30" y="40" width="100" height="50" rx="10" fill="#6366f1" />
          <path d="M50 90 l-10 18 22 -8 z" fill="#6366f1" />
          <rect x="80" y="100" width="100" height="50" rx="10" fill="#a78bfa" />
          <path d="M170 150 l10 18 -22 -8 z" fill="#a78bfa" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 200 200" className={className}>
          <circle cx="100" cy="100" r="70" fill="#7c3aed" opacity=".15" />
          <circle cx="100" cy="100" r="40" fill="#7c3aed" />
        </svg>
      );
  }
}

export default function ServicesGridStripe() {
  return (
    <section className="bg-white">
      <div className="container-x section-pad">
        <div className="text-center mb-12">
          <span className="eyebrow">What we do</span>
          <h2 className="text-3xl md:text-5xl font-extrabold mt-3 leading-tight">
            One platform, every <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-fuchsia-600">communication channel</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {SERVICES.slice(0, 9).map((s) => {
            const m = META[s.slug] || META['business-voip'];
            return (
              <Link
                key={s.slug}
                to={s.href}
                className="group relative bg-white rounded-2xl border border-slate-200 hover:border-violet-200 hover:-translate-y-1 hover:shadow-2xl hover:shadow-violet-900/5 transition-all duration-300 overflow-hidden"
              >
                {/* Top gradient stripe */}
                <div className={`h-1.5 bg-gradient-to-r ${m.stripe}`} />

                <div className="p-7 grid grid-cols-3 gap-4 items-center">
                  {/* Left: text */}
                  <div className="col-span-2">
                    <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-gradient-to-r ${m.stripe} bg-opacity-10 mb-3`}>
                      <Icon name={s.icon} size={12} className="text-white" />
                      <span className="text-[10px] font-extrabold text-white uppercase tracking-wider">{m.proof}</span>
                    </div>
                    <h3 className="text-xl font-extrabold text-slate-900 mb-1.5">{s.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4">{s.short}</p>

                    <ul className="grid grid-cols-2 gap-x-4 gap-y-1 mb-5">
                      {s.features.slice(0, 4).map((f) => (
                        <li key={f} className="flex items-center gap-1.5 text-xs text-slate-700">
                          <Check size={11} className="text-violet-600 flex-shrink-0" /> <span className="truncate">{f}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">From <strong className="text-slate-900">{m.from}</strong></span>
                      <span className="inline-flex items-center gap-1 text-sm font-bold text-violet-700 group-hover:gap-2 transition-all">
                        Learn more <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>

                  {/* Right: illustration */}
                  <div className="hidden md:block">
                    <Viz kind={m.viz} className="w-full h-32 group-hover:scale-105 transition-transform duration-500" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
